import * as THREE from 'three';

const DEFAULT_CONFIG = {
  texturePath: '/assets/2D/FX/29.png',
  duration: 0.22,
  opacityStart: 0.95,
  opacityEnd: 0.0,
  enableArcRotation: true,
  segments: 48,
  useSolidWhite: true,
  defaultSweepSpan: Math.PI * 1.05,
  trailLag: 0.28,
};

const WEAPON_TUNING = {
  oni_blade: {
    radius: 1.2, thickness: 0.44, y: 1.1, scale: 1.0, angleBias: 0.03, spawnAngleOffset: 0.0, sweepSpan: Math.PI * 0.8,
  },
  tetsu_kusarigama: {
    radius: 1.45, thickness: 0.48, y: 1.1, scale: 1.1, angleBias: -0.04, spawnAngleOffset: 0.02, sweepSpan: Math.PI * 0.86,
  },
  goryuu_warhammer: {
    radius: 1.35, thickness: 0.52, y: 1.18, scale: 1.08, angleBias: 0.06, spawnAngleOffset: 0.04, sweepSpan: Math.PI * 0.7,
  },
  kaito_shortsword: {
    radius: 1.28, thickness: 0.42, y: 1.15, scale: 1.0, angleBias: 0.0, spawnAngleOffset: 0.04, sweepSpan: Math.PI * 0.84,
  },
};

function easeOutCubic(t) {
  const clamped = Math.max(0, Math.min(1, t));
  return 1 - Math.pow(1 - clamped, 3);
}

const WORLD_UP = new THREE.Vector3(0, 1, 0);

function createAnchorMarker() {
  const marker = new THREE.Mesh(
    new THREE.SphereGeometry(0.08, 10, 10),
    new THREE.MeshBasicMaterial({ color: 0xffdd44, transparent: true, opacity: 0.95 })
  );
  marker.renderOrder = 25;
  return marker;
}

function createDirectionMarker() {
  const points = [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, 1)];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({ color: 0x66d2ff, transparent: true, opacity: 0.92 });
  const line = new THREE.Line(geometry, material);
  line.renderOrder = 25;
  return line;
}

export class SlashFxSystem {
  constructor(scene, options = {}) {
    this.scene = scene;
    this.config = { ...DEFAULT_CONFIG, ...options };
    this.textureLoader = new THREE.TextureLoader();
    this.texture = null;
    this.textureReady = false;
    this.items = [];
    this.enabled = true;
    this.debug = {
      enabled: false,
      freezeFrame: false,
      forcedRow: null,
      forcedProgress: null,
      showAnchor: false,
      slowMoFx: 1,
      wireframeArc: false,
    };
    this._yawQuat = new THREE.Quaternion();
    this._flatQuat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
    this._ensureTexture();
  }

  _ensureTexture() {
    if (this.config.useSolidWhite) {
      this.textureReady = true;
      this.texture = null;
      return;
    }
    if (this.textureReady) return;
    this.texture = this.textureLoader.load(this.config.texturePath, (loaded) => {
      loaded.colorSpace = THREE.SRGBColorSpace;
      loaded.wrapS = THREE.RepeatWrapping;
      loaded.wrapT = THREE.RepeatWrapping;
      loaded.generateMipmaps = false;
      loaded.minFilter = THREE.LinearFilter;
      loaded.magFilter = THREE.LinearFilter;
      this.textureReady = true;
    });
  }

  setDebugConfig(patch = {}) {
    Object.assign(this.debug, patch);
  }

  spawnMeleeSlash({
    weaponId = '',
    hand = 'right',
    player = null,
    aimX = 1,
    aimZ = 0,
    when = 0.2,
  } = {}) {
    if (!this.enabled || !this.textureReady || !player) return null;

    const tuning = WEAPON_TUNING[weaponId] || {};
    const safeWhen = Math.max(0, Math.min(1, when));
    const radius = tuning.radius ?? 1.2;
    const thickness = tuning.thickness ?? 0.42;
    const y = tuning.y ?? 1.1;
    const scale = tuning.scale ?? 1.0;
    const angleBias = tuning.angleBias ?? 0;
    const spawnAngleOffset = tuning.spawnAngleOffset ?? 0;
    const sweepSpan = Math.max(0.45, tuning.sweepSpan ?? this.config.defaultSweepSpan ?? Math.PI * 0.78);
    const sweepDirection = hand === 'left' ? -1 : 1;
    const centerAngle = Math.atan2(aimX, aimZ) - Math.PI / 2 + angleBias + spawnAngleOffset;
    const startAngle = centerAngle - sweepDirection * (sweepSpan * 0.5);
    const endAngle = centerAngle + sweepDirection * (sweepSpan * 0.5);
    const innerRadius = Math.max(0.05, radius - thickness * 0.5);
    const outerRadius = innerRadius + Math.max(0.05, thickness);

    const coreGeometry = new THREE.RingGeometry(innerRadius, outerRadius, this.config.segments, 1, 0, sweepSpan);
    const coreMaterial = new THREE.MeshBasicMaterial({
      map: this.texture,
      color: 0xffffff,
      transparent: true,
      opacity: this.config.opacityStart,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });

    const glowThickness = Math.max(0.06, thickness * 0.42);
    const glowGeometry = new THREE.RingGeometry(
      Math.max(0.03, innerRadius - glowThickness * 0.45),
      outerRadius + glowThickness,
      this.config.segments,
      1,
      0,
      sweepSpan
    );
    const glowMaterial = new THREE.MeshBasicMaterial({
      map: this.texture,
      color: 0xffffff,
      transparent: true,
      opacity: this.config.opacityStart * 0.45,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });

    const trailGeometry = new THREE.RingGeometry(
      Math.max(0.03, innerRadius - glowThickness * 0.2),
      outerRadius + glowThickness * 0.55,
      this.config.segments,
      1,
      0,
      sweepSpan
    );
    const trailMaterial = new THREE.MeshBasicMaterial({
      map: this.texture,
      color: 0xffffff,
      transparent: true,
      opacity: this.config.opacityStart * 0.34,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    });

    const mesh = new THREE.Mesh(coreGeometry, coreMaterial);
    const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
    const trailMesh = new THREE.Mesh(trailGeometry, trailMaterial);
    mesh.rotation.x = -Math.PI / 2;
    glowMesh.rotation.x = -Math.PI / 2;
    trailMesh.rotation.x = -Math.PI / 2;
    mesh.renderOrder = 22;
    glowMesh.renderOrder = 21;
    trailMesh.renderOrder = 20;
    mesh.scale.setScalar(scale);
    glowMesh.scale.setScalar(scale * 1.02);
    trailMesh.scale.setScalar(scale * 1.04);

    const anchor = createAnchorMarker();
    const direction = createDirectionMarker();
    anchor.visible = false;
    direction.visible = false;

    this.scene.add(mesh);
    this.scene.add(glowMesh);
    this.scene.add(trailMesh);
    this.scene.add(anchor);
    this.scene.add(direction);

    const slash = {
      mesh,
      glowMesh,
      trailMesh,
      anchor,
      direction,
      life: 0,
      duration: Math.max(0.08, this.config.duration ?? DEFAULT_CONFIG.duration),
      startAngle,
      endAngle,
      arcProgress: safeWhen,
      sweepDirection,
      radius,
      y,
      pivotRef: player,
    };

    this._applyDebugVisuals(slash);
    this._updateTransform(slash);
    this.items.push(slash);
    return slash;
  }

  _applyDebugVisuals(slash) {
    const wireframe = this.debug.enabled && this.debug.wireframeArc;
    slash.mesh.material.wireframe = wireframe;
    slash.glowMesh.material.wireframe = wireframe;
    slash.trailMesh.material.wireframe = wireframe;
  }

  _updateTransform(slash) {
    const pivotX = slash.pivotRef?.x ?? 0;
    const pivotZ = slash.pivotRef?.z ?? 0;
    const angle = this.config.enableArcRotation
      ? THREE.MathUtils.lerp(slash.startAngle, slash.endAngle, slash.arcProgress)
      : slash.startAngle;
    const trailLag = (this.config.trailLag ?? DEFAULT_CONFIG.trailLag) * (1 - slash.arcProgress * 0.7);
    const trailAngle = angle - slash.sweepDirection * trailLag;
    slash.mesh.position.set(pivotX, slash.y, pivotZ);
    slash.glowMesh.position.set(pivotX, slash.y, pivotZ);
    slash.trailMesh.position.set(pivotX, slash.y, pivotZ);
    // Lock the slash to the horizontal plane and spin only around world Y.
    this._yawQuat.setFromAxisAngle(WORLD_UP, angle);
    slash.mesh.quaternion.copy(this._yawQuat).multiply(this._flatQuat);
    slash.glowMesh.quaternion.copy(this._yawQuat).multiply(this._flatQuat);
    this._yawQuat.setFromAxisAngle(WORLD_UP, trailAngle);
    slash.trailMesh.quaternion.copy(this._yawQuat).multiply(this._flatQuat);

    const showAnchor = this.debug.enabled && this.debug.showAnchor;
    slash.anchor.visible = showAnchor;
    slash.direction.visible = showAnchor;
    if (showAnchor) {
      slash.anchor.position.set(pivotX, slash.y, pivotZ);
      slash.direction.position.set(pivotX, slash.y, pivotZ);
      slash.direction.rotation.y = angle;
      slash.direction.scale.set(slash.radius, slash.radius, slash.radius);
    }
  }

  _disposeItem(slash) {
    this.scene.remove(slash.mesh);
    this.scene.remove(slash.glowMesh);
    this.scene.remove(slash.trailMesh);
    this.scene.remove(slash.anchor);
    this.scene.remove(slash.direction);
    slash.mesh.geometry.dispose();
    slash.mesh.material.dispose();
    slash.glowMesh.geometry.dispose();
    slash.glowMesh.material.dispose();
    slash.trailMesh.geometry.dispose();
    slash.trailMesh.material.dispose();
    slash.anchor.geometry.dispose();
    slash.anchor.material.dispose();
    slash.direction.geometry.dispose();
    slash.direction.material.dispose();
  }

  clear() {
    for (const slash of this.items) this._disposeItem(slash);
    this.items.length = 0;
  }

  update(dt, camera = null) {
    if (this.items.length === 0) return;
    const slowMo = this.debug.enabled ? Math.max(0.01, this.debug.slowMoFx || 1) : 1;
    const scaledDt = dt * slowMo;
    for (let i = this.items.length - 1; i >= 0; i--) {
      const slash = this.items[i];
      const freezeFrame = this.debug.enabled && this.debug.freezeFrame;
      if (!freezeFrame) {
        slash.life += scaledDt;
        const linearT = Math.max(0, Math.min(1, slash.life / slash.duration));
        slash.arcProgress = easeOutCubic(linearT);
      } else if (Number.isFinite(this.debug.forcedProgress)) {
        slash.arcProgress = Math.max(0, Math.min(1, this.debug.forcedProgress));
      }
      const fadeT = Math.max(0, Math.min(1, slash.arcProgress));
      const baseOpacity = THREE.MathUtils.lerp(this.config.opacityStart, this.config.opacityEnd, Math.pow(fadeT, 0.9));
      const glowOpacity = THREE.MathUtils.lerp(this.config.opacityStart * 0.45, 0, Math.pow(fadeT, 0.55));
      const trailOpacity = THREE.MathUtils.lerp(this.config.opacityStart * 0.34, 0, Math.pow(fadeT, 0.45));
      const headHold = 1 - THREE.MathUtils.smoothstep(fadeT, 0.7, 1.0);
      slash.mesh.material.opacity = baseOpacity + headHold * 0.15;
      slash.glowMesh.material.opacity = glowOpacity;
      slash.trailMesh.material.opacity = trailOpacity;
      this._applyDebugVisuals(slash);

      if (!freezeFrame && slash.life >= slash.duration) {
        this._disposeItem(slash);
        this.items.splice(i, 1);
        continue;
      }

      this._updateTransform(slash);
      void camera;
    }
  }
}

export function getMeleeSlashTuning(weaponId) {
  return WEAPON_TUNING[weaponId] || null;
}
